<template>
  <div :class="$style.wrapper">
    <template v-if="!isLoading && model">
      <!-- PhotoSwipe: скрытая разметка для слайдов -->
      <div :id="pswpGalleryId" :class="$style.pswpGallery" aria-hidden="true">
        <a
          v-for="(url, idx) in model.images"
          :key="`pswp-${idx}`"
          :href="url"
          target="_blank"
          rel="noreferrer"
          :data-pswp-width="pswpSizes?.[idx]?.w || 1600"
          :data-pswp-height="pswpSizes?.[idx]?.h || 1600"
          :data-cropped="true"
        >
          {{ model.name }} {{ idx + 1 }}
        </a>
      </div>

      <!-- Карусель / фото (interactive: true — чтобы клик по фото открывал полноэкран) -->
      <DragScroll :className="$style.images" :interactive="true">
        <div
          v-for="(img, idx) in model.images"
          :key="idx"
          :class="$style.imageItem"
          role="button"
          tabindex="0"
          @click="openPhotoSwipe(idx)"
          @keydown.enter="openPhotoSwipe(idx)"
        >
          <img :src="img" alt="" draggable="false" />
        </div>
      </DragScroll>

      <div :class="$style.headerWrapper">
        <div :class="$style.header">
          <div :class="$style.headerTop">
            <h1 :class="$style.title">{{ model.name }}</h1>
          </div>
          <div :class="$style.minPrice" v-if="model.minPrice !== null">
            Минимальная стоимость:
            <span :class="$style.minPriceValue">
              {{ formatPrice(model.minPrice) }} ₽
            </span>
          </div>
        </div>
        <button :class="$style.sortBtn" type="button" @click="openSort">
          <img src="@/assets/img/market/sort.svg" alt="" />
        </button>
      </div>
      <!-- Название + мин. цена -->

      <!-- Список офферов -->
      <div :class="$style.offersList">
        <div
          v-for="offer in offers"
          :key="offer.id"
          :class="[
            $style.offerCard,
            isOfferInBid(offer.id) && $style.offerCardInBid,
          ]"
          @click="openProduct(offer.id)"
        >
          <div :class="$style.offerTags">
            <span v-if="isOfferInBid(offer.id)" :class="$style.tagInBid">
              В корзине
            </span>
            <span
              v-if="offer.inStock"
              :class="[$style.tag, $style.tagInStock]"
            >
              В наличии
            </span>
            <span v-else :class="[$style.tag, $style.tagOnOrder]">
              Под заказ
            </span>
            <span v-if="offer.isNew" :class="[$style.tag, $style.tagOrange]">
              Новое
            </span>
            <span v-if="offer.isTrade" :class="$style.tag">Торг</span>
            <span v-if="offer.pairs" :class="$style.tag">
              {{ pluralizePairs(offer.pairs) }}
            </span>
            <span v-if="offer.size" :class="$style.tag">
              {{ offer.size }}
            </span>
            <span v-if="offer.sizeCm" :class="$style.tag">
              {{ offer.sizeCm }}
            </span>
          </div>

          <div :class="$style.offerMain">
            <div>
              <div :class="$style.offerTitle">{{ model.name }}</div>
              <div :class="$style.offerPriceRow">
                <div :class="$style.offerPrice">
                  {{ formatPrice(offer.priceFrom) }} ₽
                </div>
              </div>
            </div>

            <button
              v-if="offer.isFavorite === false"
              @click.stop="changeFavorite(offer.id, offer.isFavorite)"
              :class="$style.favBtn"
              type="button"
            >
              <img src="@/assets/img/market/like.svg" alt="" />
            </button>
            <button
              v-else
              @click.stop="changeFavorite(offer.id, offer.isFavorite)"
              :class="$style.favBtn"
              type="button"
            >
              <img src="@/assets/img/market/fill-like.svg" alt="" />
            </button>
          </div>

          <button
            :class="$style.sellerRow"
            type="button"
            @click.stop="openSeller(offer.sellerId)"
          >
            <div>
              <div :class="$style.sellerName">{{ offer.sellerName }}</div>
              <div v-if="offer.rating_count > 0" :class="$style.sellerRating">
                Рейтинг:
                <span :class="$style.sellerRatingValue">
                  {{ offer.rating }}
                </span>
                <span :class="$style.sellerRatingStar">★</span>
              </div>
            </div>
            <span :class="$style.chevron">
              <img src="@/assets/img/market/chev.svg" alt="" />
            </span>
          </button>
        </div>
        <div v-if="isLoadingMore" :class="$style.loadingMore">
          <div :class="$style.loadingSpinner"></div>
        </div>
      </div>
    </template>

    <div v-else :class="$style.skeletonWrap">
      <div :class="$style.imagesSkeleton">
        <div
          v-for="n in 3"
          :key="`model-img-skeleton-${n}`"
          :class="[$style.imageSkeleton, 'skeleton']"
        ></div>
      </div>
      <div :class="$style.headerSkeleton">
        <div :class="['skeleton', $style.titleSkeleton]"></div>
        <div :class="['skeleton', $style.priceSkeleton]"></div>
      </div>
      <div :class="$style.offersSkeleton">
        <div
          v-for="n in 2"
          :key="`offer-skeleton-${n}`"
          :class="$style.offerSkeletonCard"
        >
          <div :class="['skeleton', $style.tagSkeleton]"></div>
          <div :class="['skeleton', $style.offerLineSkeleton]"></div>
          <div :class="['skeleton', $style.offerLineSkeletonShort]"></div>
          <div :class="['skeleton', $style.sellerSkeleton]"></div>
        </div>
      </div>
    </div>

    <!-- Sort Modal -->
    <div
      v-if="sortModal"
      :class="[$style.sortBg, { [$style.fadeOut]: sortClosing }]"
      @click="closeSort"
    >
      <div
        :class="[$style.sortModal, { [$style.slideDown]: sortClosing }]"
        @click.stop
      >
        <div :class="$style.sortHeader"></div>
        <div :class="$style.sortTitle">Сортировка</div>

        <div :class="$style.sortList">
          <div
            v-for="option in sortOptions"
            :key="option.value"
            :class="$style.sortItem"
            @click="applySort(option.value)"
          >
            <span>{{ option.label }}</span>
            <div
              :class="[
                $style.radio,
                { [$style.radioActive]: sort === option.value },
              ]"
            >
              <img
                v-if="sort === option.value"
                src="@/assets/img/market/check.svg"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DragScroll from "@/components/DragScroll.vue"
import PhotoSwipeLightbox from "photoswipe/lightbox"
import "photoswipe/style.css"
import { convertSize } from "@/utils/sizeChart.js"
import { pluralizePairs } from "@/utils/pluralize.js"

export default {
  name: "Model",
  components: { DragScroll },

  data() {
    return {
      model: null,
      offers: [],
      isLoading: true,
      isLoadingMore: false,
      hasMore: true,
      limit: 20,
      offset: 0,
      sort: "price_asc",
      sortModal: false,
      sortClosing: false,
      sortOptions: [
        { label: "По цене ↑", value: "price_asc" },
        { label: "По цене ↓", value: "price_desc" },
        { label: "По рейтингу ↓", value: "rating_desc" },
        { label: "По рейтингу ↑", value: "rating_asc" },
      ],
      sortBackHandlerRef: null,
      pswpGalleryId: "pswp-model-gallery",
      pswpLightbox: null,
      pswpSizes: [],
      pswpResizeHandler: null,
    }
  },

  computed: {
    modelId() {
      return this.$route.params.id
    },
    bidProductIds() {
      return this.$store.getters.bidProductIds || new Set()
    },
  },

  async mounted() {
    this.sort = this.$route.query.sort || "price_asc"
    await this.getInfo(true)
    window.addEventListener("scroll", this.handleScroll)
    this.updatePhotoSwipeCssVars()
    this.refreshPhotoSwipe()
    this.pswpResizeHandler = () => {
      this.updatePhotoSwipeCssVars()
    }
    window.addEventListener("resize", this.pswpResizeHandler)
    window.addEventListener("orientationchange", this.pswpResizeHandler)
  },
  beforeUnmount() {
    this.unregisterSortBackHandler()
    window.removeEventListener("scroll", this.handleScroll)
    this.destroyPhotoSwipe()
    if (this.pswpResizeHandler) {
      window.removeEventListener("resize", this.pswpResizeHandler)
      window.removeEventListener("orientationchange", this.pswpResizeHandler)
      this.pswpResizeHandler = null
    }
  },
  watch: {
    sortModal(value) {
      if (value) {
        this.registerSortBackHandler()
      } else {
        this.unregisterSortBackHandler()
      }
    },
  },

  methods: {
    async getInfo(reset = false) {
      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
      } else {
        if (!this.hasMore || this.isLoadingMore) return
        this.isLoadingMore = true
      }

      try {
        const response = await this.$store.dispatch("getModel", {
          id: this.modelId,
          sort: this.sort,
          limit: this.limit,
          offset: this.offset,
        })

        const data = response.data || response

        const apiModel = data.model || {}
        const sellerItems = data.sellers?.items || []

        // Обновляем модель только при первой загрузке
        if (reset) {
          let minPrice = null
          if (sellerItems.length) {
            const prices = sellerItems
              .map((s) => Number(s.price))
              .filter((n) => !Number.isNaN(n))
            minPrice = prices.length ? Math.min(...prices) : null
          }

          this.model = {
            id: apiModel.id,
            name: apiModel.title,
            images: apiModel.images || [],
            minPrice,
          }
          this.$nextTick(() => {
            this.preloadPhotoSwipeSizes(this.model?.images || [])
            this.refreshPhotoSwipe()
          })
        }

        const mappedOffers = sellerItems.map((item) => ({
          id: item.id,
          sellerId: item.seller?.id,
          sellerName: item.seller?.login || item.seller?.username || "Продавец",
          rating: item.seller?.rating ? Number(item.seller.rating) : null,
          rating_count: item.seller?.rating_count ?? 0,
          pairs: item.count,
          priceFrom: Number(item.price),
          isNew: item.is_new,
          isTrade: item.is_trade,
          isFavorite: item.is_favorite,
          // «В наличии» (true, дефолт бэка) / «Под заказ» (false) — из
          // Excel-колонки «В наличии» (ДА/НЕТ).
          inStock: item.in_stock !== false,
          avgPriceDeltaPercent:
            item.avg_price_delta_percent !== undefined &&
            item.avg_price_delta_percent !== null
              ? Number(item.avg_price_delta_percent)
              : null,
          size: item.size + " " + item.size_type,
          // Плашка сантиметража: конвертация размера в CM по общей сетке
          sizeCm: this.formatSizeCm(item.size, item.size_type),
        }))

        if (reset) {
          this.offers = mappedOffers
        } else {
          this.offers = [...this.offers, ...mappedOffers]
        }

        // Проверяем, есть ли еще данные
        this.hasMore = sellerItems.length === this.limit
        if (this.hasMore) {
          this.offset += this.limit
        }
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // Загружаем, когда осталось 200px до конца
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.getInfo(false)
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined) return ""
      return Number(value).toLocaleString("ru-RU")
    },
    pluralizePairs(count) {
      return pluralizePairs(count)
    },
    // «26,5 см» из размера (EU по умолчанию); нет конвертации — нет плашки
    formatSizeCm(size, sizeType) {
      const cm = convertSize(size, sizeType || "EU", "CM")
      if (cm === null || cm === undefined) return null
      return `${String(cm).replace(".", ",")} см`
    },
    // Эта пара уже добавлена в корзину — серая заливка карточки предложения
    isOfferInBid(offerId) {
      return this.bidProductIds.has(String(offerId))
    },
    async changeFavorite(id, isFavorite) {
      // Оптимистично обновляем состояние сразу
      const offerIndex = this.offers.findIndex((offer) => offer.id === id)
      if (offerIndex !== -1) {
        this.offers[offerIndex].isFavorite = !isFavorite
      }

      try {
        if (!isFavorite) {
          await this.$store.dispatch("addFavorite", {
            product_id: id,
          })
        } else {
          await this.$store.dispatch("deleteFavorite", {
            product_id: id,
          })
        }
      } catch (error) {
        // В случае ошибки возвращаем предыдущее состояние
        if (offerIndex !== -1) {
          this.offers[offerIndex].isFavorite = isFavorite
        }
        console.error("Ошибка при изменении избранного:", error)
      }
    },
    openProduct(id) {
      this.$router.push({
        name: "Product",
        params: { id },
      })
    },
    openSeller(id) {
      if (!id) return
      this.$router.push({
        name: "Seller",
        params: { id },
      })
    },
    updatePhotoSwipeCssVars() {
      const tg = window.Telegram?.WebApp
      const safeTop = Number(tg?.safeAreaInset?.top || 0)
      const safeBottom = Number(tg?.safeAreaInset?.bottom || 0)
      const isFullscreen = Boolean(tg?.isFullscreen)
      const tgTopOverlay = isFullscreen ? 52 : 0
      const topOffsetPx = safeTop + tgTopOverlay + 10
      const bottomOffsetPx = safeBottom + 10
      document.documentElement.style.setProperty(
        "--pswp-tg-top-offset",
        `${topOffsetPx}px`
      )
      document.documentElement.style.setProperty(
        "--pswp-tg-bottom-offset",
        `${bottomOffsetPx}px`
      )
    },
    refreshPhotoSwipe() {
      this.destroyPhotoSwipe()
      this.$nextTick(() => {
        if (!this.model?.images?.length) return
        this.initPhotoSwipe()
      })
    },
    initPhotoSwipe() {
      if (this.pswpLightbox) return
      this.pswpLightbox = new PhotoSwipeLightbox({
        gallery: `#${this.pswpGalleryId}`,
        children: "a",
        showHideAnimationType: "zoom",
        bgOpacity: 0.9,
        wheelToZoom: true,
        pswpModule: () => import("photoswipe"),
      })
      this.pswpLightbox.init()
    },
    destroyPhotoSwipe() {
      if (this.pswpLightbox) {
        this.pswpLightbox.destroy()
        this.pswpLightbox = null
      }
    },
    openPhotoSwipe(idx = 0) {
      this.updatePhotoSwipeCssVars()
      this.initPhotoSwipe()
      this.pswpLightbox?.loadAndOpen(idx)
    },
    preloadPhotoSwipeSizes(urls = []) {
      if (!urls?.length) return
      this.pswpSizes = urls.map(() => null)
      urls.forEach((url, idx) => {
        const img = new Image()
        img.onload = () => {
          this.pswpSizes[idx] = {
            w: img.naturalWidth || 1600,
            h: img.naturalHeight || 1600,
          }
        }
        img.src = url
      })
    },
    openSort() {
      this.sortClosing = false
      this.sortModal = true
    },
    closeSort() {
      this.sortClosing = true
      setTimeout(() => {
        this.sortModal = false
        this.sortClosing = false
      }, 250)
    },
    async applySort(value) {
      this.sort = value
      this.closeSort()
      await this.getInfo(true)
      // Обновляем URL с параметром сортировки
      const query = { ...this.$route.query }
      if (value !== "price_asc") {
        query.sort = value
      } else {
        delete query.sort
      }
      this.$router.replace({ query })
    },
    registerSortBackHandler() {
      if (this.sortBackHandlerRef || !window.__AppBackHandlerStack) return
      this.sortBackHandlerRef = () => {
        this.closeSort()
        return true
      }
      window.__AppBackHandlerStack.push(this.sortBackHandlerRef)
    },
    unregisterSortBackHandler() {
      if (!this.sortBackHandlerRef) return
      window.__AppBackHandlerStack?.pop(this.sortBackHandlerRef)
      this.sortBackHandlerRef = null
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0 1rem 6.5rem 1rem;
}

.images {
  margin-bottom: 0.5rem;
  display: flex;
}

.imageItem {
  flex: 0 0 auto;
  width: 8.125rem;
  height: 8.125rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    user-select: none;
    -webkit-user-drag: none;
    user-drag: none;
  }
}

.pswpGallery {
  display: none;
}

.header {
  margin-bottom: 1.25rem;
}

.headerWrapper {
  display: flex;
  justify-content: space-between;
}

.headerTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.title {
  font-size: 1rem;
  font-weight: 600;
  flex: 1;
}

.sortBtn {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;

  img {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.minPrice {
  font-size: 1rem;
  font-weight: 400;
}

.minPriceValue {
  font-weight: 600;
  margin-left: 0.5rem;
  font-size: 1rem;
}

.offersList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.offerCard {
  border-radius: 0.75rem;
  padding: 0.75rem 1rem 1rem 1rem;
  border: 1px solid #b8b8bc5c;
}

/* Пара уже в корзине — приглушённая карточка предложения */
.offerCardInBid {
  background: #f5f5f6;
  opacity: 0.75;
}

.offerTags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.625rem;
}

.tag {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: #00000008;
  color: #00000045;
}

.tagOrange {
  background: #ec66081a;
  color: #ec6608;
}

/* «В наличии» / «Под заказ» — зелёный по прецеденту tagSuccess в MyProducts,
   жёлтоватый — в той же приглушённой гамме. */
.tagInStock {
  background: #e4f8eb;
  color: #1c8a4a;
}

.tagOnOrder {
  background: #fbf3d5;
  color: #b07c00;
}

/* «В корзине» — серый чип на карточке предложения */
.tagInBid {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: #6f6f78;
  color: #ffffff;
}

.offerMain {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0.75rem;
}

.offerTitle {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.offerPriceRow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.offerPrice {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.125rem;
}

/* Price delta badge (same as Product.vue) */
.priceDelta {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.625rem;
}

.priceDelta img {
  width: 1rem;
  height: 1rem;
}

.priceDeltaText {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.25rem 0.25rem 0;
}

.priceDeltaGreenBg {
  background: rgba(16, 185, 129, 0.12);
}

.priceDeltaRedBg {
  background: rgba(239, 68, 68, 0.12);
}

.priceDeltaGreen {
  color: #10b981;
}

.priceDeltaRed {
  color: #ef4444;
}

.favBtn {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.sellerRow {
  width: 100%;
  background: #f5f7fab2;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
}

.sellerName {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.125rem;
  text-align: left;
}

.sellerRating {
  font-size: 0.875rem;
  font-weight: 400;
  text-align: left;
}

.sellerRatingValue {
  font-weight: 400;
}

.sellerRatingStar {
  color: #f7bf18;
  margin-left: 0.25rem;
}

.chevron {
  font-size: 1.25rem;
  color: #888;
}

.skeletonWrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.imagesSkeleton {
  display: flex;
  gap: 0.5rem;
}

.imageSkeleton {
  flex: 0 0 auto;
  width: 8.125rem;
  height: 8.125rem;
  border-radius: 0.25rem;
}

.headerSkeleton {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.titleSkeleton {
  height: 1.25rem;
  border-radius: 0.5rem;
}

.priceSkeleton {
  width: 60%;
  height: 1rem;
  border-radius: 0.5rem;
}

.offersSkeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.offerSkeletonCard {
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #b8b8bc5c;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tagSkeleton {
  width: 3rem;
  height: 0.75rem;
  border-radius: 0.5rem;
}

.offerLineSkeleton {
  height: 0.875rem;
  border-radius: 0.5rem;
}

.offerLineSkeletonShort {
  width: 65%;
  height: 0.75rem;
  border-radius: 0.5rem;
}

.sellerSkeleton {
  height: 2.5rem;
  border-radius: 0.625rem;
}

/* Sort Modal */
.sortBg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    backdrop-filter: blur(10px);
  }
  to {
    opacity: 0;
    backdrop-filter: blur(0);
  }
}

.fadeOut {
  animation: fadeOut 0.25s ease forwards;
}

.sortModal {
  width: 100%;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.5rem 1rem;
  animation: slideUp 0.25s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(40px);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideDownAnim {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(40px);
    opacity: 0;
  }
}

.slideDown {
  animation: slideDownAnim 0.25s ease forwards;
}

.sortHeader {
  width: 3.25rem;
  height: 0.375rem;
  background: #ddd;
  border-radius: 0.5rem;
  margin: 0 auto 0.875rem;
}

.sortTitle {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
}

.sortList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sortItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f7f7f7;
  border-radius: 0.5rem;
  cursor: pointer;
}

.radio {
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid #bbb;
}

.radioActive {
  background: #f07818;
  border-color: #f07818;
}

.radio img {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loadingMore {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* PhotoSwipe: offset controls (Telegram fullscreen + safe areas) */
:global(.pswp__top-bar) {
  top: 0;
  padding-top: 12vh;
  height: auto;
}
</style>
